import React from 'react';
import moment from 'moment';
import { FormGroup, Alert, Button, Input } from 'reactstrap';
import {
    FormLabel,
    ValidationErrorMessage,
} from '../common/forms/FormElements';
import { MomentDateFormat } from '../MomentDateFormat';
import { getEventDisplayDate } from './LongTermSchedule';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { faBellSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CancelDateInformation from './CancelDateInformation';
import { isRequestedRescheduleDateValid } from './LongTermScheduleDayCancelApiRequest';

export default class LongTermScheduleCancelManager extends React.Component {
    getActiveWorkDaysHumanReadable = () => {
        const { longTermSchedule } = this.props;

        const weekdays = [
            'Sunday',
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
            'Saturday',
        ];

        const activeDayNames = longTermSchedule.activeDays.map(
            (ad) => weekdays[ad]
        );

        return activeDayNames.join(', ');
    };

    onRescheduleDateChange = (e, fn) => {
        const { onChange } = this.props;

        onChange(e, fn);
    };

    render() {
        const {
            cancelWorkflowEnabled,
            isReleased,
            longTermSchedule,
            longTermScheduleDayCancelOptions,
            cancelRequest,
            jobCancellationTypes,
            reschedulingEffect,
            onCancellationTypeChange,
            onLongTermScheduleDayCancelOptionChange,
            onRescheduleDateChange,
            eventDate,
            saving,
            onCancelClick,
            cancelRequestRequiresTime,
            cancelRequestRequiresDateSelection,
            onChange,
            cancelRequestValid,
        } = this.props;

        if (!cancelWorkflowEnabled) {
            return null;
        }

        return (
            <div className="long-term-schedle-cancel-manager">
                <Alert color="info" fade={false}>
                    <div>
                        This long-term job is currently configured to run from{' '}
                        {moment(longTermSchedule.startDate).format(
                            MomentDateFormat.WeekDayNameAndDate
                        )}{' '}
                        to{' '}
                        {moment(longTermSchedule.endDate).format(
                            MomentDateFormat.WeekDayNameAndDate
                        )}
                        .
                    </div>
                    <div className="mt-2">
                        Its working days are{' '}
                        {this.getActiveWorkDaysHumanReadable()}.
                    </div>
                </Alert>

                <FormGroup className="long-term-schedule-day-cancel-option">
                    <FormLabel
                        htmlFor="cancelRequest.longTermScheduleDayCancelOptionId"
                        text="Reschedule"
                        required={true}
                    />
                    <ValidatedSelect
                        id="cancelRequest.longTermScheduleDayCancelOptionId"
                        name="cancelRequest.longTermScheduleDayCancelOptionId"
                        options={longTermScheduleDayCancelOptions}
                        getOptionValue={(co) => co.id}
                        getOptionLabel={(co) => co.description}
                        value={
                            longTermScheduleDayCancelOptions.filter(
                                (co) =>
                                    co.id ==
                                    cancelRequest.longTermScheduleDayCancelOptionId
                            ) ?? ''
                        }
                        onChange={onLongTermScheduleDayCancelOptionChange}
                    />
                    <ValidationErrorMessage>
                        Reschedule selection is required.
                    </ValidationErrorMessage>

                    {cancelRequestRequiresDateSelection && (
                        <div className="mt-2">
                            <FormLabel
                                htmlFor="cancelRequest.requestedRescheduleDate"
                                text="Requested Reschedule Date"
                                required={true}
                            />
                            <Input
                                id="cancelRequest.requestedRescheduleDate"
                                required
                                type="date"
                                name="cancelRequest.requestedRescheduleDate"
                                defaultValue={
                                    cancelRequest.requestedRescheduleDate ?? ''
                                }
                                onChange={onRescheduleDateChange}
                                invalid={
                                    !isRequestedRescheduleDateValid(
                                        cancelRequest,
                                        eventDate
                                    )
                                }
                                min={moment(eventDate)
                                    .add(1, 'day')
                                    .format(
                                        MomentDateFormat.DateOnlyUTCHyphenated
                                    )}
                            />
                        </div>
                    )}

                    <CancelDateInformation
                        reschedulingEffect={reschedulingEffect}
                        cancelRequest={cancelRequest}
                    />
                </FormGroup>

                {isReleased && (
                    <>
                        <FormGroup>
                            <label>Cancellation Reason</label>
                            <ValidatedSelect
                                required
                                placeholder="Select Cancellation Reason"
                                id="cancelRequest.cancellationTypeId"
                                name="cancelRequest.cancellationTypeId"
                                options={jobCancellationTypes}
                                getOptionLabel={(jct) => jct.label}
                                getOptionValue={(jct) => jct.value}
                                value={
                                    jobCancellationTypes.filter(
                                        (jact) =>
                                            jact.value ==
                                            cancelRequest.cancellationTypeId
                                    ) ?? ''
                                }
                                onChange={onCancellationTypeChange}
                            />
                        </FormGroup>

                        {cancelRequestRequiresTime && (
                            <>
                                <FormGroup>
                                    <label>Cancellation Time</label>
                                    <input
                                        id="cancelRequest.cancellationTime"
                                        required
                                        type="datetime-local"
                                        className="form-control"
                                        name="cancelRequest.cancellationTime"
                                        defaultValue={
                                            cancelRequest.cancellationTime ?? ''
                                        }
                                        onChange={onChange}
                                    />
                                    <small className="invalid-feedback text-danger">
                                        Cancellation Time is required.
                                    </small>
                                </FormGroup>
                                <FormGroup>
                                    <label>Notes</label>
                                    <textarea
                                        id="cancelRequest.cancellationNote"
                                        name="cancelRequest.cancellationNote"
                                        className="form-control"
                                        defaultValue={
                                            cancelRequest.cancellationNote ?? ''
                                        }
                                        onChange={onChange}
                                        placeholder="Enter optional notes regarding the cancellation."
                                        type="text"
                                        maxLength="500"
                                        rows="5"
                                    />
                                </FormGroup>
                            </>
                        )}
                    </>
                )}

                <Button
                    type="button"
                    color="danger"
                    className="w-100 xl"
                    onClick={onCancelClick}
                    disabled={!cancelRequestValid || saving}
                >
                    <FontAwesomeIcon
                        icon={faBellSlash}
                        className="mr-2"
                    ></FontAwesomeIcon>
                    Process cancellation for {getEventDisplayDate(eventDate)}
                </Button>
            </div>
        );
    }
}
